import { daysBetween } from './dates';
import { hashSeed, mulberry32 } from './random';

export type Confidence = 'baixa' | 'moderada' | 'alta';

export interface ChanceInput {
  /** Histórico diário da rota, do mais antigo ao mais recente. Lacunas são aceitas. */
  history: readonly { day: string; price: number }[];
  current: number;
  target: number;
  /** Hoje (AAAA-MM-DD). */
  today: string;
  /** Data do voo de ida (AAAA-MM-DD). */
  departure: string;
  /** Teto do horizonte em dias. Padrão 30. */
  maxHorizonDays?: number;
  simulations?: number;
  /** Texto que entra na semente para estabilizar o resultado por rota. */
  seedKey?: string;
}

export interface ChanceResult {
  /** 0–100. */
  chance: number;
  confidence: Confidence;
  horizonDays: number;
  /** Base da estimativa em linguagem simples, ex.: "62 dias de histórico". */
  basis: string;
}

/** Mínimo de variações diárias para arriscar uma porcentagem. Abaixo disso a tela diz que falta histórico. */
export const MIN_RETURNS = 14;
/** Últimos dias antes do voo, quando o preço costuma subir. */
export const LATE_WINDOW_DAYS = 21;
/** Alta média por dia dentro da janela final (premissa conservadora, ver ADR-0005). */
export const LATE_DRIFT_PER_DAY = 0.004;

/**
 * Chance de o preço tocar o alvo dentro do horizonte.
 *
 * Modelo (ADR-0005): bootstrap das variações logarítmicas diárias observadas na própria rota,
 * centradas (sem tendência herdada do passado), mais uma alta determinística quando o caminho
 * entra nos últimos 21 dias antes do voo. Conta em quantos caminhos simulados o preço encosta no alvo.
 * Retorna null quando não há histórico suficiente ou não há horizonte (voo muito perto).
 */
export function chanceToReachTarget(input: ChanceInput): ChanceResult | null {
  if (!(input.target > 0)) return null;
  const sim = simulateMinima(input);
  if (!sim) return null;
  const { confidence, horizonDays, basis } = sim;
  if (input.current <= input.target) return { chance: 100, confidence, horizonDays, basis };
  return { chance: chanceFromMinima(sim.minima, input.target), confidence, horizonDays, basis };
}

export type SimulationInput = Omit<ChanceInput, 'target'>;

export interface Simulation {
  /** Menor preço (log) de cada caminho simulado, em ordem crescente. */
  minima: Float64Array;
  confidence: Confidence;
  horizonDays: number;
  basis: string;
}

/**
 * Simula os caminhos do preço e guarda o menor valor de cada um. A semente não depende do alvo:
 * a mesma simulação responde a qualquer alvo, então a chance cresce sempre com o alvo e as sugestões
 * (targets.ts) batem com a chance que aparece depois de escolhidas.
 */
export function simulateMinima(input: SimulationInput): Simulation | null {
  const { current, today, departure } = input;
  if (!(current > 0)) return null;

  const daysToDeparture = daysBetween(today, departure);
  const horizonDays = Math.min(input.maxHorizonDays ?? 30, daysToDeparture - 1);
  if (horizonDays < 1) return null;

  const returns = dailyLogReturns(input.history);
  if (returns.length < MIN_RETURNS) return null;

  const mean = returns.reduce((s, r) => s + r, 0) / returns.length;
  const centered = returns.map((r) => r - mean);
  const n = input.simulations ?? 4000;
  const rand = mulberry32(hashSeed(`${input.seedKey ?? ''}|${today}|${current}`));

  const minima = new Float64Array(n);
  for (let s = 0; s < n; s++) {
    let logP = Math.log(current);
    let min = Number.POSITIVE_INFINITY;
    for (let d = 1; d <= horizonDays; d++) {
      const r = centered[Math.floor(rand() * centered.length)]!;
      const late = daysToDeparture - d <= LATE_WINDOW_DAYS ? LATE_DRIFT_PER_DAY : 0;
      logP += r + late;
      if (logP < min) min = logP;
    }
    minima[s] = min;
  }
  minima.sort();
  return {
    minima,
    confidence: confidenceFor(returns.length, horizonDays),
    horizonDays,
    basis: `${input.history.length} dias de histórico`,
  };
}

/** Porcentagem (0–100) dos caminhos cujo menor preço encosta no alvo. */
export function chanceFromMinima(minima: Float64Array, target: number): number {
  const logTarget = Math.log(target);
  let lo = 0;
  let hi = minima.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (minima[mid]! <= logTarget) lo = mid + 1;
    else hi = mid;
  }
  return Math.round((lo / minima.length) * 100);
}

/** Variação log por dia; quando há lacuna de k dias, a variação é dividida igualmente entre eles. */
export function dailyLogReturns(history: readonly { day: string; price: number }[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < history.length; i++) {
    const a = history[i - 1]!;
    const b = history[i]!;
    const gap = daysBetween(a.day, b.day);
    if (gap < 1 || !(a.price > 0) || !(b.price > 0)) continue;
    const r = Math.log(b.price / a.price) / gap;
    for (let k = 0; k < Math.min(gap, 7); k++) out.push(r);
  }
  return out;
}

export function confidenceFor(returnCount: number, horizonDays: number): Confidence {
  // Pouco histórico para um horizonte longo reduz a confiança.
  const ratio = returnCount / Math.max(horizonDays, 1);
  if (returnCount >= 60 && ratio >= 2) return 'alta';
  if (returnCount >= 30 && ratio >= 1) return 'moderada';
  return 'baixa';
}

/** Texto do horizonte no formato do ChanceMeter: "em 30 dias". */
export function horizonLabel(horizonDays: number): string {
  return horizonDays === 1 ? 'em 1 dia' : `em ${horizonDays} dias`;
}
